import { useState } from 'react'
import { AlertCircle, AlertTriangle, CheckCircle2, Loader2, X, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import type { UploadedFileTracker } from './hooks/useUploadTracker'

interface UploadProgressStripProps {
  files: UploadedFileTracker[]
  allDone: boolean
  onDismiss: () => void
  onReprocessDuplicate: (trackerId: string, force: boolean) => Promise<void>
  onCancelDuplicate: (trackerId: string) => void
}

export function UploadProgressStrip({
  files,
  allDone,
  onDismiss,
  onReprocessDuplicate,
  onCancelDuplicate,
}: UploadProgressStripProps) {
  if (files.length === 0) return null

  return (
    <div className="rounded-md border border-neutral-200 bg-white px-4 py-3 shadow-sm">
      <div className="flex items-center justify-between pb-2">
        <p className="text-sm font-medium text-neutral-700">
          {allDone ? 'Upload complete' : 'Processing uploads…'}
        </p>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-neutral-500 disabled:opacity-50"
          onClick={onDismiss}
          disabled={!allDone}
          title={allDone ? 'Dismiss' : 'Waiting for uploads to finish'}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-2">
        {files.map((file) => (
          <UploadProgressRow
            key={file.id}
            file={file}
            onReprocess={onReprocessDuplicate}
            onSkip={onCancelDuplicate}
          />
        ))}
      </div>
    </div>
  )
}

interface RowProps {
  file: UploadedFileTracker
  onReprocess: (trackerId: string, force: boolean) => Promise<void>
  onSkip: (trackerId: string) => void
}

function UploadProgressRow({ file, onReprocess, onSkip }: RowProps) {
  const [acknowledged, setAcknowledged] = useState(false)
  const [busy, setBusy] = useState(false)

  const isDuplicate = file.status === 'duplicate'
  const hasCommitted = file.duplicate?.has_committed_links ?? false
  const committedCount = file.duplicate?.committed_link_count ?? 0
  const txnCount = file.duplicate?.transaction_count ?? 0
  const reprocessDisabled = busy || (hasCommitted && !acknowledged)

  const handleReprocess = async () => {
    setBusy(true)
    try {
      await onReprocess(file.id, hasCommitted)
    } finally {
      setBusy(false)
      setAcknowledged(false)
    }
  }

  return (
    <div className="rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2">
      <div className="flex items-center gap-3">
        {file.status === 'uploading' && (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-blue-500" />
        )}
        {file.status === 'processing' && (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-amber-500" />
        )}
        {file.status === 'completed' && (
          <CheckCircle2 className="h-4 w-4 shrink-0 text-green-500" />
        )}
        {file.status === 'failed' && (
          <XCircle className="h-4 w-4 shrink-0 text-red-500" />
        )}
        {file.status === 'duplicate' && (
          <AlertCircle className="h-4 w-4 shrink-0 text-blue-500" />
        )}

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-neutral-900">{file.filename}</p>
          <p className="text-xs text-neutral-500">
            {file.status === 'uploading' && (file.message || 'Uploading...')}
            {file.status === 'processing' && 'Processing — extracting transactions...'}
            {file.status === 'completed' &&
              `Done — ${file.transactionCount ?? 0} transactions extracted`}
            {file.status === 'failed' && (file.message || 'Processing failed')}
            {file.status === 'duplicate' &&
              (txnCount > 0
                ? `Already uploaded — ${txnCount} transaction(s) on file`
                : 'Already uploaded')}
          </p>
        </div>

        {isDuplicate && (
          <div className="flex shrink-0 items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-7"
              onClick={() => onSkip(file.id)}
              disabled={busy}
            >
              Skip
            </Button>
            <Button
              variant="default"
              size="sm"
              className="h-7"
              onClick={handleReprocess}
              disabled={reprocessDisabled}
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Reprocess'}
            </Button>
          </div>
        )}
      </div>

      {isDuplicate && hasCommitted && (
        <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
          <div className="flex-1 text-xs text-amber-800">
            <p className="font-medium">Committed to Mirage</p>
            <p className="mt-0.5">
              {committedCount} transaction(s) from this file are committed. Reprocessing may
              cause data inconsistency in your accounting system.
            </p>
            <label className="mt-2 flex cursor-pointer items-center gap-2">
              <Checkbox
                checked={acknowledged}
                onCheckedChange={(checked) => setAcknowledged(checked === true)}
                disabled={busy}
              />
              <span>I understand the risks and want to proceed</span>
            </label>
          </div>
        </div>
      )}
    </div>
  )
}
